<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <ToastMessage />

      <FormTitle>{{ $t('location.title') }}</FormTitle>
      <FormContent>
        <div :class="$style.wrapper">
          <ZipCityLocations
            :selectedZip="selectedZip"
            :selectedCity="selectedCity"
            :label="t('valuationForm.address.postcode')"
            :isDirty="v$.selectedZip.$dirty || v$.selectedCity.$dirty"
            :isInvalid="v$.selectedZip.$error || v$.selectedCity.$error"
            transparentBackground
            :class="$style.spacing"
            @locationChanged="onLocationChanged"
          >
            <template #errorMessages>
              <span class="error-message">
                {{ t('location.errorMessage.postCode.missing') }}
              </span>
            </template>
          </ZipCityLocations>

          <FormBottom :onNext="onNext" :nextButtonLabel="$t('location.nextButtonLabel')" />
        </div>
      </FormContent>
    </TransitionWrapper>
  </FormContainer>
</template>

<script lang="ts" setup>
import {
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  ToastMessage,
  TransitionWrapper,
  ZipCityLocations,
} from '@/components/common';
import { ZipAndCity } from '@/components/common/ZipCityLocations/types';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { Location } from '@/stores/types/propertyValuation';
import { getStepInputs } from '@/tracking/tracking';
import { ValuationFlow } from '@/types';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t, locale } = useI18n();
const propertyValuationStore = usePropertyValuationStore();
const { data: formData, step: formStep } = storeToRefs(propertyValuationStore);
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
const { query } = useRoute();

const selectedZip = ref<string>(formData.value.address?.postalCode ?? '');
const selectedCity = ref<string>(formData.value.address?.city ?? '');

const validationRules = {
  selectedZip: { required },
  selectedCity: { required },
};
const v$ = useVuelidate(validationRules, { selectedZip, selectedCity });

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.PostalCode);
};

const onNext = async (): Promise<void> => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const location: Location = {
    postalCode: String(v$.value.selectedZip.$model),
    city: String(v$.value.selectedCity.$model),
  };

  propertyValuationStore.setFormData({
    address: location,
  });
  propertyValuationStore.navigateNext(ValuationFlow.PostalCode);
};

onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_location_postcode',
    locale: locale.value,
    stepInputs: getStepInputs(formData.value, (query.source as string) ?? '', formStep.value),
  });
});

const onLocationChanged = (value: Partial<ZipAndCity>) => {
  selectedCity.value = value.city ?? '';
  selectedZip.value = value.zip ?? '';
  v$.value.selectedCity.$touch();
  v$.value.selectedZip.$touch();
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper {
  position: relative;
}
</style>
